import CircularProgress from "@mui/material/CircularProgress";
import clsx from "clsx";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSwitchgearsBySubstation } from "./data/Assets/useSwitchgearsBySubstation";
import { CreateableDropdown } from "./shared/CreateableDropdown";
import { Panel } from "./shared/interfaces/Switchgear";
import { MessageToast, ToastMode } from "./shared/MessageToast";
import { Snackbar } from "./shared/Snackbar";
import { ValidationMessage } from "./shared/ValidationMessage";

import { AccessControlContext } from "./contexts/AccessControlContext";
import { CreateSwitchgearWithPanelsDialog } from "./hvSubstations/CreateSwitchgearWithPanelsDialog";
import { AssetTypeKeys } from "./hvSubstations/models/AssetTypeKeys";
import { Asset } from "./shared/interfaces/Asset";

interface Props {
  substationId?: number;
  onSwitchgearChange: (
    selectedSwitchgearId?: number,
    switchgearName?: string,
    index?: number,
    assetType?: string,
  ) => void;
  selectedSwitchgearId?: number;
  invalid?: boolean;
  invalidMessage?: string;
  createable?: boolean;
  switchgearCreationPrepopulatedPanels?: Panel[];
  register?: UseFormRegister<FieldValues>;
  formState?: FormState<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  initialiseFormState?: boolean;
  customerId?: number;
  width?: string;
  index?: number;
  assetType?: string;
}

export const SwitchgearDropdown = React.forwardRef((props: Props & DropdownProps, ref: React.Ref<Dropdown>) => {
  const { t } = useTranslation();

  const accessControlContext = useContext(AccessControlContext);
  const userCanEditHvSubstation = accessControlContext.canI("edit", "hvSubstations", props.customerId);

  const {
    substationId,
    selectedSwitchgearId,
    onSwitchgearChange,
    register,
    formState,
    setValue,
    initialiseFormState,
    ...rest
  } = props;

  const switchgears = useSwitchgearsBySubstation(props.substationId);

  const [switchgearNameAutocompleteValue, setSwitchgearNameAutocompleteValue] = useState<string | undefined>();
  const [createSwitchgearDialogOpen, setCreateSwitchgearDialogOpen] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  function handleChange(e: any) {
    if (e) {
      const switchgear = switchgears.data?.find((s) => s.id === e.value);
      setValue && setValue(`switchgear`, e.value, { shouldValidate: true });
      onSwitchgearChange(e.value, switchgear?.name || "", props.index, switchgear?.assetTypeKey);
    } else {
      onSwitchgearChange(undefined, undefined, props.index, undefined);
    }
  }

  useEffect(() => {
    initialiseFormState && setValue && substationId && setValue(`switchgear`, selectedSwitchgearId);
  }, [selectedSwitchgearId, setValue, substationId, initialiseFormState]);

  function handleCreateSwitchgearClick(text: string | undefined) {
    setSwitchgearNameAutocompleteValue(text);
    setCreateSwitchgearDialogOpen(true);
  }

  function handleCreateDialogClose() {
    setCreateSwitchgearDialogOpen(false);
  }

  const filterSwitchgearsByAssetType = (switchgear: Asset) => {
    const assetTypeKey = switchgear.assetTypeKey as keyof typeof AssetTypeKeys;
    return AssetTypeKeys[assetTypeKey] === (props.assetType ?? AssetTypeKeys.Switchgear);
  };

  const handleSwitchgearCreated = useCallback(
    (switchgearId: number | undefined, name: string) => {
      switchgears.refetch().then((s) => {
        const createdSwitchgear = s.data?.find(
          (switchgear) => switchgear.id === switchgearId || switchgear.name === name,
        );
        onSwitchgearChange(createdSwitchgear?.id, createdSwitchgear?.name, props.index);
      });
      setSnackbarText(
        t("common.dataSuccessfullyCreated", {
          entity: t("asset.asset"),
          id: name,
        }),
      );
      setShowSnackbar(true);
      setCreateSwitchgearDialogOpen(false);
    },
    [onSwitchgearChange, props.index, switchgears, t],
  );

  if (switchgears.isLoading) {
    return <CircularProgress size={24} />;
  } else if (switchgears.error) {
    return <MessageToast text={t("common.dataFetchError", { val: "$t(asset.asset)" })} mode={ToastMode.Error} show />;
  } else
    return (
      <div className="d-flex justify-content-start">
        <div className="d-flex flex-column w-100">
          {props.createable && userCanEditHvSubstation ? (
            <CreateableDropdown
              value={switchgears.data?.find((s) => s.id === selectedSwitchgearId)}
              options={switchgears.data?.filter(filterSwitchgearsByAssetType) || []}
              onChange={handleChange}
              optionLabelField="name"
              optionValueField="id"
              placeholder={t("common.select", { item: "$t(asset.asset)" })}
              createOptionLabel={t("asset.addAsset")}
              onCreateOptionClick={handleCreateSwitchgearClick}
              invalid={props.invalid}
              key={selectedSwitchgearId || "undefined"}
              style={{ width: props.width || 300 }}
            />
          ) : register ? (
            <div>
              <Dropdown
                className={formState && formState.errors["switchgear"] ? "p-invalid" : undefined}
                disabled={!substationId}
                value={selectedSwitchgearId}
                optionValue="id"
                options={switchgears.data}
                optionLabel="name"
                filter
                filterBy="name"
                placeholder={t("common.select", { item: "$t(asset.asset)" })}
                {...register(`switchgear`, { value: selectedSwitchgearId, required: true })}
                ref={ref}
                onChange={handleChange}
                style={{ width: props.width || 200 }}
                {...rest}
              />

              {formState && formState.errors["switchgear"] && (
                <ValidationMessage
                  invalidMessage={t("common.pleaseEnter", { val: "$t(asset.asset)" })}
                  validationType="error"
                  showIcon
                />
              )}
            </div>
          ) : (
            <Dropdown
              ref={ref}
              className={clsx({ "p-invalid": props.invalid })}
              disabled={!substationId}
              value={selectedSwitchgearId}
              optionValue="id"
              options={switchgears.data}
              onChange={handleChange}
              optionLabel="name"
              filter
              filterBy="name"
              placeholder={t("common.select", { item: "$t(asset.asset)" })}
              style={{ width: props.width || 200 }}
              {...rest}
            />
          )}

          {props.invalid && props.invalidMessage && (
            <ValidationMessage invalidMessage={props.invalidMessage} validationType="error" showIcon />
          )}

          {props.substationId && createSwitchgearDialogOpen && (
            <CreateSwitchgearWithPanelsDialog
              substationId={props.substationId}
              existingSwitchgearNames={switchgears.data?.map((switchgear) => switchgear.name)}
              visible={createSwitchgearDialogOpen}
              onClose={handleCreateDialogClose}
              onSuccess={handleSwitchgearCreated}
              prepopulatedName={switchgearNameAutocompleteValue}
              prepopulatedPanels={props.switchgearCreationPrepopulatedPanels}
              assetType={props.assetType}
            />
          )}
        </div>

        <Snackbar
          open={showSnackbar}
          onClose={() => {
            setShowSnackbar(false);
          }}
          message={snackbarText}
        />
      </div>
    );
});
